/**
 * The Assistant's per-account state that outlives one AssistantPane render
 * (outlook-orachat-uplift s5, s6): the conversation per open item and a session
 * generation that Sign out — or signing in as another account (s6 CR-11) — bumps.
 *
 *  - Resolved thread conversations are kept per `${pane}:${itemKey}` (s6,
 *    design C33): coming back to an item re-mounts its conversation without
 *    asking the server again.
 *  - `fallbackConversationFor` is the conversation for an item the server could
 *    not resolve (plan AD-1), and for a compose window without a thread (one
 *    per window, C34): a fresh id per item, kept for this sign-in.
 *  - The last chat user seen, so a sign-in can tell whether the ACCOUNT changed
 *    (s6 CR-11).
 *
 * Light on purpose: App calls `resetAssistantSession` on Sign out without
 * loading the chat chunk, so this module never imports the panel.
 */

import { useSyncExternalStore } from "react";

import { newCorrelationId } from "../obs/logger";
import { chatSignIn } from "../oraChat/bootOraChat";
import { clearCurrentThread, type ThreadPane } from "./threadConversation";

/** A thread conversation the server resolved (C33). */
export interface ResolvedThread {
  conversationId: string;
  existing: boolean;
  lastActivityAt: string | null;
}

const resolvedByKey = new Map<string, ResolvedThread>();
/** One fallback conversation per open item, for the life of this pane's sign-in (AD-1). */
const fallbackByItem = new Map<string, string>();
/** The oid of the last chat user the pane loaded in this window. */
let lastUserOid: string | null = null;

/** A new conversation id, UUID-shaped like every other add-in id. */
export function newConversationId(): string {
  return `conv-${newCorrelationId()}`;
}

/** The conversation the pane falls back to for `itemKey` (AD-1; compose without a thread). */
export function fallbackConversationFor(itemKey: string): string {
  let id = fallbackByItem.get(itemKey);
  if (!id) {
    id = newConversationId();
    fallbackByItem.set(itemKey, id);
  }
  return id;
}

/** The thread conversation already resolved for (pane, item), if any. */
export function resolvedThreadFor(pane: ThreadPane, itemKey: string): ResolvedThread | null {
  return resolvedByKey.get(`${pane}:${itemKey}`) ?? null;
}

export function rememberResolvedThread(pane: ThreadPane, itemKey: string, thread: ResolvedThread): void {
  resolvedByKey.set(`${pane}:${itemKey}`, thread);
}

/** The pane loaded this user (CR-11 compares a later sign-in against it). */
export function rememberChatUser(oid: string): void {
  if (oid) lastUserOid = oid;
}

/** The oid of the last chat user the pane loaded, or null. */
export function lastChatUserOid(): string | null {
  return lastUserOid;
}

function forgetConversations(): void {
  resolvedByKey.clear();
  fallbackByItem.clear();
  lastUserOid = null;
  clearCurrentThread();
}

type Listener = () => void;
let generation = 0;
const listeners = new Set<Listener>();

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function currentGeneration(): number {
  return generation;
}

/** Bumped by every Sign out and account change: the pane re-reads its user when it changes. */
export function useAssistantSessionGeneration(): number {
  return useSyncExternalStore(subscribe, currentGeneration, currentGeneration);
}

/**
 * Sign out, or a sign-in as ANOTHER account (CR-11): forget the previous
 * account's chat. The conversations are dropped (a new account never continues
 * the old one), the chat sign-in flag is cleared (the next read decides it
 * afresh), and an open pane re-reads the signed-in user and resolves its thread
 * again — which, with the token gone, shows the sign-in card.
 */
export function resetAssistantSession(): void {
  forgetConversations();
  chatSignIn.markSignedIn();
  generation += 1;
  listeners.forEach((listener) => listener());
}

/** Test helper: forget the pane's conversations, its last user and the mounted thread. */
export function _resetAssistantConversationsForTests(): void {
  forgetConversations();
}
